import { useNavigator } from '@repo/toolkit/use-navigator'
import { PlusIcon, TravelIcon, TravelTwoIcon, UnpublishIcon, UploadIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME, MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { assignTripGroupingsToTrips } from '../../api/trips/assign-trip-groupings-to-trips'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import SearchableDataTable from '../../components/elements/searchable-data-table'
import UnpublishJourneyModal from '../../components/pages/trips/unpublish-journey-modal'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { Trip, TripFilters } from '../../lib/types/models/trip/trip'
import { TripGrouping } from '../../lib/types/models/trip-grouping/trip-grouping'
import {
  transformTripFiltersToUrl,
  transformUrlToTripFilters
} from '../../lib/utils/filters-mapping/trips'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { getTripGroupingColumns } from '../trip-groupings/utils'
import TripFilter from './trip-filter'
import { getTripColumns } from './utils'

export const tripInitialFilter: TripFilters = {
  transportMeans: [],
  periods: [],
  countryCodes: [],
  departures: [],
  arrivals: []
}

const TripListPage = () => {
  const { navigate, navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<TripFilters>({
      initialFilters: tripInitialFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformTripFiltersToUrl,
      transformUrlToFilter: transformUrlToTripFilters
    })

  const tableRef = React.useRef<DynamicTableRef<Trip>>(null)

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'filters' | 'assign-to-group' | 'unpublish',
    Trip[]
  >(['filters', 'assign-to-group', 'unpublish'])

  const {
    mutate: assignTripGroupingsToTripsMutation,
    isPending: isAssignTripGroupingsToTripsPending
  } = useMutation({
    mutationFn: assignTripGroupingsToTrips,
    onSuccess: () => {
      toast.success('Groupes de voyage ajoutés avec succès')
      toggleModal('assign-to-group')
      tableRef.current?.refetch()
    },
    onError: showAxiosError
  })

  return (
    <div>
      <UserNavBar title={'Voyages'} icon={TravelTwoIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<Trip, TripFilters>
        ref={tableRef}
        title={'Tous les voyages'}
        onLoadingChange={setIsTableLoading}
        deleteMessage={{
          name: 'voyages',
          attributeName: 'id',
          deleteOne: 'Êtes-vous sûr de vouloir supprimer ce voyage ?',
          deleteMany: 'Êtes-vous sûr de vouloir supprimer ces voyages ?',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer tous les voyages ?'
        }}
        columns={getTripColumns('FULL')}
        selectable
        allowSearchParam
        onRowClick={(trip, event) =>
          navigateWithEvent(Paths.TRIP_UPDATE_PAGE.replace(':id', trip.id.toString()), event)
        }
        endpoint={API.trips.getAll.url}
        onFiltersClick={() => toggleModal('filters')}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            variant: 'primary',
            label: 'Ajouter un nouveau voyage',
            fn: () => navigate(Paths.TRIP_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: UnpublishIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Dépublier',
            fn: (trips) => {
              toggleModal('unpublish', trips)
            }
          },
          {
            type: 'multiple',
            icon: PlusIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Ajouter à des groupes',
            fn: (trips) => {
              toggleModal('assign-to-group', trips)
            }
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Exporter',
            fn: (trips) => {
              const ids = trips.map((trip) => trip.id)
              downloadDocuments({
                endpoint: API.trips.exportExcel.url,
                ids
              })
            }
          }
        ]}
      />

      {/* Filters Modal */}
      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <TripFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {selectedData && (
        <Modal
          isOpen={modalState['assign-to-group']}
          title={'Ajouter à des groupes de voyage'}
          icon={TravelIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('assign-to-group')}
        >
          <SearchableDataTable<TripGrouping, null>
            className={MODAL_BODY_CLASSNAME}
            selectedRows={selectedData.flatMap((t) => t.belongsTo)}
            columns={getTripGroupingColumns('SUMMARY')}
            description={`Vous pouvez choisir plus d'un groupe de voyages:`}
            endpoint={API.tripGroupings.getAll.url}
            onCancel={() => closeModel('assign-to-group')}
            onSelect={(tripGroupings) => {
              assignTripGroupingsToTripsMutation({
                tripIds: selectedData.map((t) => t.id),
                tripGroupingIds: tripGroupings.map((tg) => tg.id)
              })
            }}
            confirmationText="Ajouter"
            isLoading={isAssignTripGroupingsToTripsPending}
          />
        </Modal>
      )}

      {selectedData && (
        <Modal
          isOpen={modalState.unpublish}
          title={'Dépublier'}
          icon={UnpublishIcon}
          size="medium"
          onCancel={() => closeModel('unpublish')}
          hideActions
          hasBorderBottom
        >
          <UnpublishJourneyModal
            className={MODAL_CONTENT_CLASSNAME}
            trips={selectedData.map((t) => t.id)}
            onCancel={() => closeModel('unpublish')}
          />
        </Modal>
      )}
    </div>
  )
}

export default TripListPage
